import { HttpRequest } from "../../../../services/httpRequest";

// Códigos con los que el app-backend responde cuando el crédito NO puede pagar (ver
// CreditPaymentErrorCode en app-backend). Cualquier otro error es técnico.
//  - CREDIT_NOT_ENABLED_FOR_PAYMENT: el crédito aún no está habilitado (sin desembolso).
//  - NOVATION_NOT_AVAILABLE: el crédito sí puede pagar, pero ya no puede hacer la novación.
export const CREDIT_NOT_ENABLED_CODE = "CREDIT_NOT_ENABLED_FOR_PAYMENT";
export const NOVATION_NOT_AVAILABLE_CODE = "NOVATION_NOT_AVAILABLE";

const PAYMENT_BLOCKED_CODES = [
  CREDIT_NOT_ENABLED_CODE,
  NOVATION_NOT_AVAILABLE_CODE,
  "CREDIT_NOT_FOUND",
];

// true si la respuesta de un endpoint /payments/* es un rechazo de negocio (no un fallo técnico) —
// en ese caso hay que mostrar el mensaje y cortar el flujo, NO reintentar ni caer a otra pasarela.
export const isPaymentBlocked = (response) =>
  response?.error === true && PAYMENT_BLOCKED_CODES.includes(response?.code);

// Consulta si el crédito puede recibir este pago. `hasNovation` indica el tipo de pago: true para la
// novación ("Pago mínimo"), false para el pago total y el abono — la novación tiene una restricción
// extra (solo desde 8 días antes de la fecha límite y hasta 30 días después) que los otros pagos no
// tienen. Devuelve:
//   { ok: true }                                  → puede pagar
//   { ok: false, blocked: true, code, message }   → rechazo de negocio (`code` dice cuál)
//   { ok: false, blocked: false, message }        → fallo técnico (red, 5xx)
// Los botones previos al paso final (opciones de pago, "Abonar ahora") solo bloquean con
// `blocked: true`; ante un fallo técnico dejan seguir porque el paso final vuelve a validar en el backend.
export async function checkCreditPayable(
  creditId = localStorage.getItem("credit_id"),
  hasNovation = false,
) {
  const response = await HttpRequest.validateCreditPayment(
    creditId,
    hasNovation,
  );
  if (!response.error) return { ok: true, blocked: false };
  return {
    ok: false,
    blocked: isPaymentBlocked(response),
    code: response.code,
    message: response.message,
  };
}

// Qué opciones de pago mostrar al cargar la pantalla. Una sola consulta como novación: el backend revisa
// primero si el crédito puede pagar y después la ventana de novación, así que el código de rechazo
// dice cuál de las dos falló.
//   canPay: false solo si el crédito aún no está habilitado para pagar (sin desembolso) — no se muestra
//           ninguna opción, solo el mensaje.
//   canNovate: false si además está fuera de la ventana — se oculta solo el "Pago mínimo".
// Ante cualquier otra respuesta (fallo técnico, crédito ajeno) se muestra todo: ocultar opciones por un
// fallo de red le quitaría al cliente algo que sí tiene, y cada clic vuelve a validar en el backend.
export async function getPaymentAvailability(
  creditId = localStorage.getItem("credit_id"),
) {
  const result = await checkCreditPayable(creditId, true);
  const canPay = result.code !== CREDIT_NOT_ENABLED_CODE;
  return {
    canPay,
    canNovate: canPay && result.code !== NOVATION_NOT_AVAILABLE_CODE,
    message: result.message,
  };
}
